import { faChevronDown, faChevronLeft } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FC, ReactNode, useState, useEffect } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { debounce } from "lodash"; // Import debounce from lodash
import Loading from "../Loading";
import SearchBar from "../table/SearchBar";

export interface SelectOption {
  value: string | number;
  label: string;
  code?: string;
}

export interface SelectProps {
  name: string;
  label?: string;
  options: SelectOption[];
  isMulti?: boolean;
  placeholder?: string | ReactNode;
  isRequired?: boolean;
  withSearch?: boolean;
  isLoading?: boolean;
  searchFullSize?: boolean;
  isDisable?: boolean;
  customError?: any;
  onSearchChange?: (term: string) => void; // Optional prop for async search
  onChange?: (value: any) => void;
}

const SelectInput: FC<SelectProps> = ({
  name,
  label,
  options,
  isMulti = false,
  isRequired,
  placeholder,
  withSearch,
  isLoading,
  searchFullSize,
  isDisable = false,
  customError,
  onSearchChange, // Optional prop for async search
  onChange,
}) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedOptions, setSelectedOptions] = useState<SelectOption[]>([]);
  const [isOpen, setIsOpen] = useState(false);

  // Debounce the search term internally
  const debouncedSearchTerm = debounce((term: string) => {
    if (onSearchChange) {
      onSearchChange(term); // Call the external handler if provided
    }
  }, 300); // Adjust debounce time as needed

  // Update debounced search term when searchTerm changes
  useEffect(() => {
    debouncedSearchTerm(searchTerm);
    return () => debouncedSearchTerm.cancel(); // Cleanup debounce on unmount
  }, [searchTerm]);

  const handleSelect = (option: SelectOption, field: any) => {
    if (isDisable) return; // Prevent selection if disabled
    // if (isMulti) {
    //   setSelectedOptions((prev) => (prev.some((selected) => selected.value === option.value) ? prev.filter((selected) => selected.value !== option.value) : [...prev, option]));
    //   setIsOpen(false);
    //   setSearchTerm("");
    // } else {
    //   setSelectedOptions([option]);
    //   setIsOpen(false);
    //   setSearchTerm("");
    // }
    if (isMulti) {
      const currentValue = field.value || [];
      const newValue = currentValue.some((selected: SelectOption) => selected.value === option.value) ? currentValue.filter((selected: SelectOption) => selected.value !== option.value) : [...currentValue, option];
      field.onChange(newValue);
      onChange?.(newValue);
    } else {
      field.onChange(option);
      onChange?.(option);
    }
    setIsOpen(false);
    setSearchTerm("");
  };

  const handleSearchChange = (term: string) => {
    setSearchTerm(term); // Update local search term
  };

  const handleClear = () => {
    setSelectedOptions([]);
  };

  const filteredOptions = options.filter((option) => option.label.toLowerCase().includes(searchTerm.toLowerCase()));

  const getDisplayValue = (field: any) => {
    if (!field.value) return placeholder || t("selectPlaceholder");

    if (isMulti) {
      if (!Array.isArray(field.value)) return placeholder || t("selectPlaceholder");
      return field.value.length > 0 ? field.value.map((option: SelectOption) => option.label).join(", ") : placeholder || t("selectPlaceholder");
    }

    return field.value?.label || placeholder || t("selectPlaceholder");
  };

  return (
    <div className="flex flex-col gap-2 mb-3">
      {label && (
        <label htmlFor={name} className="text-sm font-medium text-text mb-0">
          {t(label)}
          {isRequired && <span className="text-danger"> *</span>}
        </label>
      )}
      <Controller
        name={name}
        control={control}
        render={({ field }) => {
          return (
            <div className="relative">
              <div
                onClick={() => (!isDisable && !isLoading ? setIsOpen(!isOpen) : () => {})}
                className={`parent-animation-on-hover bg-inputBg w-full px-3 py-2 border border-inputBg rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary sm:text-sm flex justify-between items-center ${
                  isDisable ? "cursor-not-allowed opacity-50" : "cursor-pointer"
                }`}
              >
                {/* {field?.value?.label ? field?.value?.label : selectedOptions.length > 0 ? selectedOptions.map((option) => option.label).join(", ") : <span className="text-gray-400">{placeholder || t("selectPlaceholder")}</span>} */}
                <span className={!field?.value ? "text-gray-400" : ""}>{getDisplayValue(field)}</span>
                <FontAwesomeIcon icon={isOpen ? faChevronLeft : faChevronDown} className="w-3 shake-on-hover" />
              </div>
              {isOpen && !isDisable && (
                <div className="absolute z-10 mt-1 w-full bg-inputBg border border-inputBg rounded-md shadow-lg max-h-40 overflow-auto">
                  {withSearch && <SearchBar searchTerm={searchTerm} onSearchChange={handleSearchChange} isFullSize={searchFullSize} color="sidebar" />}
                  {isLoading && (
                    <div className="px-2 py-3">
                      <Loading size="w-4 h-4" />
                    </div>
                  )}
                  {/* {filteredOptions.map((option) => (
                    <div
                      key={option.value}
                      onClick={() => {
                        handleSelect(option);
                        field.onChange(isMulti ? selectedOptions : option);
                        handleSearchChange("");
                        onChange?.(isMulti ? selectedOptions : option);
                      }}
                      className={`cursor-pointer px-3 py-2 hover:bg-hoverPrimary transition300 ${selectedOptions.some((selected) => selected.value === option.value) ? "bg-primary text-text" : "text-text"}`}
                    >
                      {option.label}
                    </div>
                  ))} */}
                  {filteredOptions.map((option) => (
                    <div
                      key={option.value}
                      onClick={() => handleSelect(option, field)}
                      className={`cursor-pointer px-3 py-2 hover:bg-hoverPrimary transition300 ${
                        (isMulti ? (field.value || []).some((selected: SelectOption) => selected.value === option.value) : field.value?.value === option.value) ? "bg-primary text-text" : "text-text"
                      }`}
                    >
                      {option.label}
                    </div>
                  ))}
                </div>
              )}
              {/* {isMulti && selectedOptions.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {selectedOptions.map((option) => (
                    <div key={option.value} className="bg-[#2d2d2d] text-white px-2 py-1 rounded-md flex items-center">
                      {option.label}
                      <span onClick={() => handleSelect(option)} className="ml-2 cursor-pointer text-danger">
                        ×
                      </span>
                    </div>
                  ))}
                  <button onClick={handleClear} className="bg-[#2d2d2d] text-white px-2 py-1 rounded-md" type="button">
                    Clear
                  </button>
                </div>
              )} */}
              {isMulti && field.value?.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {field.value.map((option: SelectOption) => (
                    <div key={option.value} className="bg-[#2d2d2d] text-white px-2 py-1 rounded-md flex items-center">
                      {option.label}
                      <span onClick={() => handleSelect(option, field)} className="ml-2 cursor-pointer text-danger">
                        ×
                      </span>
                    </div>
                  ))}
                  <button
                    onClick={() => {
                      field.onChange([]);
                      onChange?.([]);
                    }}
                    className="bg-[#2d2d2d] text-white px-2 py-1 rounded-md"
                    type="button"
                  >
                    Clear
                  </button>
                </div>
              )}
            </div>
          );
        }}
      />
      {customError && <p className="text-sm text-danger -mt-1">{customError}</p>}
      {errors[name] && <p className="text-sm text-danger -mt-1">{String(errors[name]?.message)}</p>}
    </div>
  );
};

export default SelectInput;
